<template>
  <div class="editorial-a-wrap">

    <header class="ed-header">
      <div class="left-section">
        <button type="button" class="hamburger-menu" aria-label="Toggle menu">☰</button>
        <nav class="lang-switcher" aria-label="Language">
          <button type="button" class="lang-btn active" aria-pressed="true">EN</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">DE</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">FR</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">IT</button>
        </nav>
      </div>
      <div class="brand">
        <span class="brand-title">Swiss Eating Quiz</span>
        <span class="brand-version">Beta · 03.05.2026</span>
      </div>
    </header>

    <div class="screen-wrap">
      <main class="screen" role="main" aria-labelledby="question-heading">

        <div class="meta-row">
          <button
            type="button"
            class="back-button"
            :disabled="currentIndex === 0"
            aria-label="Zurück"
            @click="goBack"
          >←</button>
          <span class="question-counter">
            {{ paddedIndex }} / {{ paddedTotal }}
          </span>
        </div>

        <div
          class="progress-bar"
          role="progressbar"
          :aria-valuenow="currentIndex + 1"
          aria-valuemin="1"
          :aria-valuemax="questions.length"
          aria-label="Fortschritt"
        >
          <div
            v-for="seg in progressSegments"
            :key="seg.sectionId"
            class="progress-segment"
            :style="{ flex: seg.size }"
          >
            <div
              v-if="seg.fill > 0"
              class="progress-segment-fill"
              :style="{ width: (seg.fill * 100) + '%' }"
            ></div>
          </div>
        </div>

        <p class="section-eyebrow">
          {{ currentSection.label }} · Frage {{ withinSectionIndex + 1 }} von {{ currentSection.count }}
        </p>

        <div class="question-block">
          <h1 id="question-heading" class="question-title">{{ currentQuestion.title }}</h1>
          <p class="question-prompt">{{ currentQuestion.prompt }}</p>
        </div>

        <button
          v-if="currentQuestion.tooltip"
          type="button"
          class="tooltip-button"
          :aria-expanded="String(tooltipOpen)"
          :aria-controls="tooltipId"
          @click="tooltipOpen = !tooltipOpen"
        >
          <span class="tooltip-icon" aria-hidden="true">i</span>
          Was zählt hier?
        </button>

        <div
          v-if="currentQuestion.tooltip && tooltipOpen"
          :id="tooltipId"
          class="tooltip-content"
        >
          {{ currentQuestion.tooltip }}
        </div>

        <fieldset class="answer-fieldset" :key="currentQuestion.id">
          <legend class="sr-only">Antwortoptionen</legend>
          <label
            v-for="opt in currentQuestion.options"
            :key="opt.value"
            class="answer-option"
          >
            <input
              type="radio"
              :name="`q-${currentQuestion.id}`"
              :value="opt.value"
              v-model="answers[currentQuestion.id]"
            >
            <span class="answer-option-label">{{ opt.label }}</span>
          </label>
        </fieldset>

        <div class="footer">
          <button
            type="button"
            class="next-button"
            :disabled="!hasAnswer"
            @click="goNext"
          >{{ isLast ? 'Fertig' : 'Weiter' }}</button>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import { sections, questions } from '../_data/sampleQuestions.js';

function pad2(n) {
  return n < 10 ? `0${n}` : String(n);
}

export default {
  name: 'EditorialA',
  data() {
    return {
      questions,
      currentIndex: 0,
      tooltipOpen: false,
      answers: {},
    };
  },
  computed: {
    sectionsWithCounts() {
      return sections
        .map(s => {
          let firstIdx = -1;
          let lastIdx = -1;
          let count = 0;
          this.questions.forEach((q, i) => {
            if (q.sectionId === s.id) {
              if (firstIdx === -1) firstIdx = i;
              lastIdx = i;
              count++;
            }
          });
          return { ...s, count, firstIdx, lastIdx };
        })
        .filter(s => s.count > 0);
    },
    currentQuestion() {
      return this.questions[this.currentIndex];
    },
    currentSection() {
      return this.sectionsWithCounts.find(s => s.id === this.currentQuestion.sectionId);
    },
    withinSectionIndex() {
      return this.currentIndex - this.currentSection.firstIdx;
    },
    progressSegments() {
      return this.sectionsWithCounts.map(s => {
        let fill = 0;
        if (this.currentIndex > s.lastIdx) {
          fill = 1;
        } else if (this.currentIndex >= s.firstIdx) {
          fill = (this.currentIndex - s.firstIdx + 1) / s.count;
        }
        return { sectionId: s.id, size: s.count, fill };
      });
    },
    hasAnswer() {
      return this.answers[this.currentQuestion.id] !== undefined;
    },
    isLast() {
      return this.currentIndex === this.questions.length - 1;
    },
    tooltipId() {
      return `tooltip-q${this.currentQuestion.id}`;
    },
    paddedIndex() {
      return pad2(this.currentIndex + 1);
    },
    paddedTotal() {
      return pad2(this.questions.length);
    },
  },
  methods: {
    goBack() {
      if (this.currentIndex === 0) return;
      this.currentIndex--;
      this.tooltipOpen = false;
    },
    goNext() {
      if (!this.hasAnswer) return;
      if (this.isLast) {
        this.$router.push('/postsurvey/email');
        return;
      }
      this.currentIndex++;
      this.tooltipOpen = false;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

.editorial-a-wrap {
  --swiss-red: #DA291C;
  --swiss-red-deep: #B01E13;
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-faint: #9C9C9C;
  --paper: #FAF7F2;
  --paper-warm: #F2EDE4;
  --paper-card: #FFFFFF;
  --rule-soft: #E5E0D7;
  --good: #2D8659;
  --good-soft: rgba(45, 134, 89, 0.10);
  --font-display: "Fraunces", Georgia, serif;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "JetBrains Mono", "SF Mono", Consolas, monospace;

  font-family: var(--font-body);
  background: var(--paper);
  color: var(--ink);
  line-height: 1.55;
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  min-height: 100%;
  box-sizing: border-box;
}

.editorial-a-wrap *,
.editorial-a-wrap *::before,
.editorial-a-wrap *::after {
  box-sizing: border-box;
}

.ed-header {
  border-bottom: 1px solid var(--ink);
  background: var(--paper);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.left-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hamburger-menu {
  background: transparent;
  border: 0.5px solid var(--rule-soft);
  color: var(--ink);
  font-size: 18px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
}

.lang-switcher {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.04em;
}

.lang-btn {
  background: transparent;
  border: none;
  color: var(--ink-faint);
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  padding: 2px 4px;
}

.lang-btn.active {
  color: var(--swiss-red);
  font-weight: 600;
}

.lang-sep {
  color: var(--ink-faint);
}

.brand {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.brand-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.brand-version {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-mute);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.screen-wrap {
  display: flex;
  justify-content: center;
  padding: 28px 20px 40px;
}

.screen {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
}

.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 18px;
  color: var(--ink-mute);
  line-height: 1;
  margin-left: -8px;
}

.back-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.question-counter {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 36px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--rule-soft);
}

.progress-segment {
  height: 3px;
  background: var(--paper-warm);
  overflow: hidden;
}

.progress-segment-fill {
  height: 100%;
  background: var(--swiss-red);
  transition: width 0.3s ease;
}

.section-eyebrow {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--swiss-red);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-eyebrow::before {
  content: "";
  width: 24px;
  height: 1px;
  background: var(--swiss-red);
}

.question-block {
  margin-bottom: 28px;
}

.question-title {
  font-family: var(--font-display);
  font-size: clamp(36px, 8vw, 48px);
  font-weight: 400;
  line-height: 1.0;
  letter-spacing: -0.02em;
  margin: 0 0 18px;
  font-variation-settings: "opsz" 144;
  color: var(--ink);
}

.question-title em {
  font-style: italic;
  font-weight: 300;
  color: var(--swiss-red);
}

.question-prompt {
  font-size: 16px;
  line-height: 1.5;
  color: var(--ink-soft);
  margin: 0;
}

.tooltip-button {
  background: transparent;
  border: 0.5px solid var(--rule-soft);
  border-radius: 0;
  padding: 8px 12px;
  font-size: 12px;
  color: var(--ink-mute);
  cursor: pointer;
  align-self: flex-start;
  margin-bottom: 28px;
  font-family: var(--font-body);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: border-color 0.15s, color 0.15s;
}

.tooltip-button:hover {
  border-color: var(--ink);
  color: var(--ink);
}

.tooltip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--swiss-red);
  color: white;
  font-size: 10px;
  font-weight: 600;
  font-style: italic;
  font-family: var(--font-display);
  line-height: 1;
}

.tooltip-content {
  background: var(--paper-warm);
  border-left: 2px solid var(--swiss-red);
  padding: 14px 16px;
  font-size: 14px;
  color: var(--ink-soft);
  margin-bottom: 28px;
  line-height: 1.55;
}

.answer-fieldset {
  border: none;
  padding: 0;
  margin: 0 0 32px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.answer-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 4px;
  border-bottom: 0.5px solid var(--rule-soft);
  cursor: pointer;
  transition: padding-left 0.15s, color 0.15s;
  position: relative;
}

.answer-option:first-child {
  border-top: 0.5px solid var(--rule-soft);
}

.answer-option:hover {
  padding-left: 8px;
}

.answer-option:has(input:checked) {
  padding-left: 12px;
}

.answer-option:has(input:checked)::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--swiss-red);
}

.answer-option input[type="radio"] {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border: 1px solid var(--ink-faint);
  border-radius: 50%;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
  transition: border-color 0.15s;
}

.answer-option input[type="radio"]:checked {
  border-color: var(--swiss-red);
}

.answer-option input[type="radio"]:checked::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: var(--swiss-red);
}

.answer-option-label {
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--ink);
  flex: 1;
  line-height: 1.4;
}

.answer-option:has(input:checked) .answer-option-label {
  color: var(--ink);
  font-weight: 500;
}

.footer {
  margin-top: auto;
  padding-top: 16px;
}

.next-button {
  width: 100%;
  padding: 16px;
  background: var(--ink);
  color: var(--paper);
  border: 1px solid var(--ink);
  border-radius: 0;
  font-size: 14px;
  font-family: var(--font-mono);
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.next-button:disabled {
  background: var(--paper-warm);
  color: var(--ink-faint);
  border-color: var(--rule-soft);
  cursor: not-allowed;
}

.next-button:not(:disabled):hover {
  background: var(--swiss-red);
  border-color: var(--swiss-red);
}
</style>
